import { useGetDevice, useGetDocuments, useGetUser } from "@hooks";
import { Chip, Skeleton } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { DocumentScopeChip } from "src/components/Documents/DocumentScopeChip";
import { RenameDocumentDialog } from "src/components/Documents/RenameDocumentDialog";
import DocVersionDropdown from "src/components/DocViewer/components/docViewerTopBar/selects/DocVersionDropdown";
import { useGetDocumentsTemplates } from "src/hooks/queries";
import {
  canDocHaveMultipleInstances,
  getDocVersionStatus,
  isRevision,
} from "../../../../utils";
import ApproveButton from "./actions/ApproveButton";
import { DeletedDocumentMoreOptions } from "./actions/DeletedDocumentMoreOptions";
import DiffButton from "./actions/DiffButton";
import DocViewerMoreOptions from "./actions/DocViewerMoreOptions";
import EditButton from "./actions/EditButton";
import NonDiffMoreOptions from "./actions/NonDiffMoreOptions";
import { DocViewerTopBarProps } from "./DocViewerTopBar.types";
import DocRevisionDropDown from "./selects/DocRevisionDropDown";

export const DocViewerTopBar = ({
  docProps,
  handleFileDownload,
  onDeleteChangesDocument,
  handleNewRecordFromFile,
  fileInput,
}: DocViewerTopBarProps) => {
  const { data: user } = useGetUser();
  const { orgId = "", deviceId = "" } = useParams();
  const [renameDocumentDialogOpen, setRenameDocumentDialogOpen] =
    useState(false);

  if (!docProps) {
    return null;
  }

  const {
    docs,
    hasOutstandingAction,
    isCreatingNewInstance,
    loadingNewDocVersion,
    onCreateDocInstance,
    onDocInfoButtonClick,
    onSelectDocInstance,
    onSelectDocVersion,
    selectedDoc,
    selectedVersion,
    isDocEnabled,
    onEditClick,
    isDiffMode,
    onToggleDiff,
  } = docProps;
  const isDeleted = Boolean(selectedDoc.softDeletionEvent);

  const { data: documents } = useGetDocuments(
    orgId,
    selectedDoc.deviceId || deviceId,
    { enabled: !isDeleted }
  );
  const queryDeviceId = selectedDoc.deviceId || deviceId;
  const { data: device } = useGetDevice({
    orgId,
    deviceId: queryDeviceId,
  });
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId: queryDeviceId,
    enabled: !isDeleted,
  });

  if (!user || (!isDeleted && (!device || !documents))) {
    return null;
  }

  const canHaveMultipleInstances = canDocHaveMultipleInstances({
    document: selectedDoc,
    documents: documents ?? [],
  });

  const docVersionStatus = getDocVersionStatus({
    selectedDocVersion: selectedVersion,
    document: selectedDoc,
    user,
    orgId,
  });

  const showBarRightSide = selectedVersion && !isDeleted;

  const shouldShowDocRevisionDropdown =
    !isDeleted && canHaveMultipleInstances && docs;

  const isDraft = docVersionStatus === "DRAFT";
  const isArchived = docVersionStatus === "ARCHIVED";

  const selectedDocIsRevision = selectedVersion
    ? isRevision(selectedVersion)
    : false;

  const newestVersionIsDraft = selectedDoc.versions[0]?.revision === null;

  const shouldShowEditButton =
    !isDraft && !isArchived && !(selectedDocIsRevision && isDiffMode);

  const documentScopeLabel = selectedDoc.deviceId
    ? device?.name.trim() || "Device"
    : "Global";

  return (
    <div className="flex items-center justify-between py-2 px-3 border-0 border-b border-solid border-gray-200 min-h-[49px]">
      <div className="flex min-w-0 items-center gap-2">
        {shouldShowDocRevisionDropdown && (
          <DocRevisionDropDown
            selectedDoc={selectedDoc}
            isDocEnabled={isDocEnabled}
            docs={docs}
            onCreateDocInstance={onCreateDocInstance}
            onSelectDocInstance={onSelectDocInstance}
            isCreatingNewInstance={isCreatingNewInstance}
            {...(handleNewRecordFromFile &&
              fileInput && {
                handleNewRecordFromFile,
                fileInput,
              })}
            standalone
          />
        )}

        {loadingNewDocVersion || !selectedVersion ? (
          <Skeleton width={200} height={34} className="transform-none" />
        ) : (
          <DocVersionDropdown
            document={selectedDoc}
            selectedVersion={selectedVersion}
            onSelectDocVersion={onSelectDocVersion}
            isDocEnabled={isDocEnabled}
            isDiffMode={isDiffMode}
          />
        )}

        {isDeleted && <Chip color="error" label="Deleted" size="small" />}

        <div className="flex min-w-0 items-center">
          <DocumentScopeChip
            dataTestId="document-viewer-scope-chip"
            label={documentScopeLabel}
            maxWidth={220}
          />
        </div>

        {!isDeleted && (
          <div>
            <DiffButton
              selectedVersion={selectedVersion}
              onToggleDiff={onToggleDiff}
              isDiffMode={isDiffMode || false}
            />
          </div>
        )}
      </div>
      {selectedVersion && isDeleted && (
        <div className="flex items-center gap-2">
          <DeletedDocumentMoreOptions documentId={selectedDoc.id} />
        </div>
      )}
      {showBarRightSide && (
        <div className="flex items-center gap-2">
          {shouldShowEditButton && (
            <EditButton
              docVersionStatus={docVersionStatus}
              isDocEnabled={isDocEnabled}
              loadingNewDocVersion={loadingNewDocVersion}
              onEditClick={onEditClick}
              selectedDoc={selectedDoc}
              hasDraft={newestVersionIsDraft}
            />
          )}

          {isDiffMode && (
            <DocViewerMoreOptions
              isDisabled={!isDocEnabled}
              normalButtons={[
                {
                  title: "Rename document",
                  onClick: () => setRenameDocumentDialogOpen(true),
                },
                ...(handleFileDownload
                  ? [
                      {
                        title: "Download File",
                        onClick: handleFileDownload,
                      },
                    ]
                  : []),
              ]}
              dangerZoneButtons={[
                ...(onDeleteChangesDocument
                  ? [
                      {
                        title: "Delete File",
                        onClick: onDeleteChangesDocument,
                        tooltipTitle: !selectedDocIsRevision
                          ? "Delete the changes document"
                          : "Cannot delete if attached to live revision",
                        disabled: selectedDocIsRevision,
                      },
                    ]
                  : []),
              ]}
            />
          )}
          {!isDiffMode && (
            <NonDiffMoreOptions
              {...(handleFileDownload && { handleFileDownload })}
              selectedDoc={selectedDoc}
              selectedVersion={selectedVersion}
              isDocEnabled={isDocEnabled}
              templateConfig={templatesConfig?.[selectedDoc.templateKey ?? ""]}
            />
          )}

          {onDocInfoButtonClick && !isDiffMode && (
            <ApproveButton
              hasOutstandingAction={hasOutstandingAction}
              onDocInfoButtonClick={onDocInfoButtonClick}
              isDocEnabled={isDocEnabled}
            />
          )}
        </div>
      )}
      {renameDocumentDialogOpen && (
        <RenameDocumentDialog
          deviceId={selectedDoc.deviceId ?? deviceId}
          document={selectedDoc}
          onClose={() => setRenameDocumentDialogOpen(false)}
          orgId={orgId}
        />
      )}
    </div>
  );
};

export default DocViewerTopBar;
